import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Spinner } from 'reactstrap';
import moment from 'moment-timezone';
import { 
    get2FAStatus, 
    getTrustedDevices, 
    disable2FA, 
    removeTrustedDevice, 
} from '../../slices/two-factor.slice';
import TwoFactorSetup from '../TwoFactorSetup';
import PasswordInput from '../PasswordInput';
import './TwoFactorSettings.scss';

const TwoFactorSettings = () => {
    const dispatch = useDispatch();
    const { 
        isEnabled, 
        isLoading, 
        backupCodesCount, 
        trustedDevicesCount, 
        lastVerified, 
        trustedDevices, 
        error 
    } = useSelector(state => state.twoFactor);
    
    const [showSetup, setShowSetup] = useState(false);
    const [showDisable, setShowDisable] = useState(false);
    const [showDevices, setShowDevices] = useState(false);
    const [disableForm, setDisableForm] = useState({
        password: '',
        verificationCode: ''
    });

    useEffect(() => {
        dispatch(get2FAStatus());
    }, [dispatch]);

    useEffect(() => {
        if (showDevices) {
            dispatch(getTrustedDevices());
        }
    }, [showDevices, dispatch]);

    const handleSetupComplete = () => {
        setShowSetup(false);
        dispatch(get2FAStatus());
    };

    const handleDisable2FA = async (e) => {
        e.preventDefault();
        
        try {
            await dispatch(disable2FA({
                password: disableForm.password,
                token: disableForm.verificationCode
            })).unwrap();
            
            setShowDisable(false);
            setDisableForm({ password: '', verificationCode: '' });
            dispatch(get2FAStatus());
        } catch (error) {
            console.error('Failed to disable 2FA:', error);
        }
    };

    const handleRemoveDevice = async (deviceId) => {
        if (window.confirm('Are you sure you want to remove this trusted device?')) {
            try {
                await dispatch(removeTrustedDevice(deviceId)).unwrap();
                dispatch(getTrustedDevices());
            } catch (error) {
                console.error('Failed to remove device:', error);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return moment(dateString).format('MMM D, YYYY h:mm A');
    };

    const getTimeSince = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).fromNow();
    };

    return (
        <div className="two-factor-settings">
            <Card className="security-card">
                <CardHeader>
                    <h5>
                        <i className="fas fa-shield-alt security-icon"></i>
                        Two-Factor Authentication
                    </h5>
                </CardHeader>
                <CardBody>
                    {error && (
                        <Alert color="danger" className="mb-3">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {error}
                        </Alert>
                    )}

                    <div className="status-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                {isEnabled ? (
                                    <span className="status-enabled">
                                        <i className="fas fa-check-circle"></i>
                                        2FA is enabled
                                    </span>
                                ) : (
                                    <span className="status-disabled">
                                        <i className="fas fa-times-circle"></i>
                                        2FA is not enabled
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        <p className="text-muted">
                            {isEnabled 
                                ? 'Your account is protected with two-factor authentication. You\'ll need to enter a code from your authenticator app when signing in or performing sensitive actions.'
                                : 'Add an extra layer of security to your account by enabling two-factor authentication.'
                            }
                        </p>
                    </div>

                    {isEnabled && (
                        <div className="stats-row">
                            <div className="stat-item">
                                <div className="stat-number">{backupCodesCount}</div>
                                <div className="stat-label">Backup codes remaining</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-number">{trustedDevicesCount}</div>
                                <div className="stat-label">Trusted devices</div>
                            </div>
                        </div>
                    )}

                    {isEnabled && lastVerified && (
                        <div className="mb-3">
                            <small className="text-muted">
                                Last verified: {formatDate(lastVerified)} ({getTimeSince(lastVerified)})
                            </small>
                        </div>
                    )}

                    <div className="action-buttons">
                        {!isEnabled ? (
                            <Button 
                                color="primary" 
                                onClick={() => setShowSetup(true)}
                                disabled={isLoading}
                            >
                                <i className="fas fa-plus me-2"></i>
                                Enable 2FA
                            </Button>
                        ) : (
                            <>
                                <Button 
                                    color="info" 
                                    onClick={() => setShowDevices(true)}
                                    disabled={isLoading}
                                >
                                    <i className="fas fa-devices me-2"></i>
                                    Manage Devices ({trustedDevicesCount})
                                </Button>
                                <Button 
                                    color="danger" 
                                    onClick={() => setShowDisable(true)}
                                    disabled={isLoading}
                                >
                                    <i className="fas fa-times me-2"></i>
                                    Disable 2FA
                                </Button>
                            </>
                        )}
                    </div>

                    {backupCodesCount <= 2 && isEnabled && (
                        <div className="warning-section">
                            <i className="fas fa-exclamation-triangle warning-icon"></i>
                            <p className="warning-text">
                                You have {backupCodesCount} backup codes remaining. Consider regenerating them soon.
                            </p>
                        </div>
                    )}
                </CardBody>
            </Card>

            <TwoFactorSetup 
                isOpen={showSetup} 
                toggle={() => setShowSetup(false)}
                onComplete={handleSetupComplete}
            />

            <Modal isOpen={showDisable} toggle={() => setShowDisable(false)}>
                <ModalHeader toggle={() => setShowDisable(false)}>
                    Disable Two-Factor Authentication
                </ModalHeader>
                <ModalBody>
                    <Alert color="warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Warning:</strong> Disabling 2FA will make your account less secure. 
                        You'll only need your password to sign in.
                    </Alert>
                    
                    <Form onSubmit={handleDisable2FA} className="disable-2fa-form">
                        <FormGroup>
                            <Label for="currentPassword">Current Password</Label>
                            <PasswordInput
                                id="currentPassword"
                                value={disableForm.password}
                                onChange={(e) => setDisableForm(prev => ({
                                    ...prev,
                                    password: e.target.value
                                }))}
                                className="password-input"
                                required
                            />
                        </FormGroup>
                        
                        <FormGroup>
                            <Label for="verificationCode">2FA Verification Code</Label>
                            <Input
                                type="text"
                                id="verificationCode"
                                value={disableForm.verificationCode}
                                onChange={(e) => setDisableForm(prev => ({
                                    ...prev,
                                    verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                                }))}
                                placeholder="000000"
                                maxLength={6}
                                className="verification-input"
                                required
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="secondary" 
                        onClick={() => setShowDisable(false)}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        color="danger" 
                        onClick={handleDisable2FA}
                        disabled={isLoading || !disableForm.password || disableForm.verificationCode.length !== 6}
                    >
                        {isLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Disabling...
                            </>
                        ) : (
                            'Disable 2FA'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showDevices} toggle={() => setShowDevices(false)} size="lg">
                <ModalHeader toggle={() => setShowDevices(false)}>
                    Trusted Devices
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted">
                        These devices are trusted for 60 days and won't require 2FA verification during that time.
                    </p>
                    
                    <div className="trusted-devices">
                        {trustedDevices.length === 0 ? (
                            <div className="no-devices">
                                <i className="fas fa-mobile-alt mb-2" style={{fontSize: '2rem', opacity: 0.3}}></i>
                                <p>No trusted devices found.</p>
                            </div>
                        ) : (
                            trustedDevices.map((device, index) => (
                                <div key={device.deviceId} className="device-item">
                                    <div className="device-info">
                                        <div className="device-name">
                                            <i className="fas fa-desktop me-2"></i>
                                            {device.deviceName}
                                        </div>
                                        <div className="device-meta">
                                            Added: {formatDate(device.createdAt)} • 
                                            Last used: {formatDate(device.lastUsed)}
                                        </div>
                                    </div>
                                    <div className="device-actions">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => handleRemoveDevice(device.deviceId)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowDevices(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default TwoFactorSettings;
